import React, { useEffect, useState } from 'react';
import {
  combineLatest, filter, Subject, takeUntil, tap,
} from 'rxjs';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Container, i18n } from '../../../../symphony';
import { LandingPageHeader } from './LandingPageHeader/LandingPageHeader';
import { WhatYouLearn } from './WhatYouLearn/WhatYouLearn';
import { CourseDescription } from './CourseDescription/CourseDescription';
import { ActionType, RequestType } from '../../global-interface';
import { ReasonsSection } from './ReasonsSection/ReasonsSection';
import { TestimonialsSection } from './TestimonialsSection/TestimonialsSection';
import { GoogleHire } from './GoogleHire/GoogleHire';
import { BlogSection } from './BlogSection/BlogSection';
import { LandingPageFooter } from './LandingPageFooter/LandingPageFooter';
import { LandingPageCourse } from './LandingPageCourse/LandingPageCourse';
import { WhyAngular } from './WhyAngular/WhyAngular';
import { WhySololearn } from './WhySololearn/WhySololearn';
import { AngularCourseContent } from './AngularCourseContent/AngularCourseContent';
import { AngularCourseFooter } from './AngularCourseFooter/AngularCourseFooter';
import { AICourseSkills } from './AICourseSkills/AICourseSkills';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlLEContext } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import { WhatYouLearnAngular } from './WhatYouLearnAngular/WhatYouLearnAngular';
import { useComponentWillMount } from '../../../../shared/public/SlUtils/useComponentWillMount';
import { SlHelmet } from '../../../../shared/public/SlHelmet/SlHelmet';
import {
  ILandingPageResponse, LandingPageSection, ILandingPageMetaType, LandingPageData,
  ILandingPageSections,
} from '../../../../api/private/learn-engine-api/landing-page-api.interface';
import { ICourseStructureDTO } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlFooter } from '../../../../navigation/public/SlFooter/SlFooter';

import s from './LandingPage.scss';
import { getEnvironment } from '../../../../../client/utils/environment';

type LPComponent = (props: {
  data?: LandingPageData;
  alias?: string;
}) => React.ReactNode;

const Components: Record<LandingPageSection, LPComponent> = {
  [LandingPageSection.Header]: LandingPageHeader,
  [LandingPageSection.WhatYouLearn]: WhatYouLearn,
  [LandingPageSection.CourseDescription]: CourseDescription,
  [LandingPageSection.ReasonsSection]: ReasonsSection,
  [LandingPageSection.CourseSection]: LandingPageCourse,
  [LandingPageSection.TestimonialsSection]: TestimonialsSection,
  [LandingPageSection.BlogSection]: BlogSection,
  [LandingPageSection.Footer]: LandingPageFooter,
  [LandingPageSection.GoogleHire]: GoogleHire,
  [LandingPageSection.WhyAngular]: WhyAngular,
  [LandingPageSection.WhySololearn]: WhySololearn,
  [LandingPageSection.WhatYouLearnAngular]: WhatYouLearnAngular,
  [LandingPageSection.AngularCourseContent]: AngularCourseContent,
  [LandingPageSection.AngularCourseFooter]: AngularCourseFooter,
  [LandingPageSection.AICourseSkills]: AICourseSkills,
};

interface ILandingPageProps {
  alias: string;
}

export function LandingPage({ alias }: ILandingPageProps): JSX.Element {
  useStyles(s);
  const { actions$ } = Container.take(SlLEContext, SlActions);

  useComponentWillMount(() => {
    actions$.next({
      type: ActionType.OnLandingPageMount,
      requestType: RequestType.Parallel,
      data: {
        alias,
      },
    });
  });

  const { landingPageData$, courseStructure$ } = Container.take(
    SlLEContext,
    SlDataService,
  );

  const [data, setData] = useState<ILandingPageResponse>(
    landingPageData$.value,
  );

  const [courseStructure, setCourseStructure] = useState<ICourseStructureDTO>(
    courseStructure$.value,
  );

  useEffect(() => {
    const unsubscriber: Subject<void> = new Subject<void>();

    combineLatest([
      landingPageData$.pipe(tap(setData)),
      courseStructure$.pipe(tap(setCourseStructure)),
    ])
      .pipe(
        takeUntil(unsubscriber),
        filter((d) => !!d),
      )
      .subscribe(([lp, course]) => {
        if (lp && course) {
          actions$.next({
            type: ActionType.OnLandingPageIsMounted,
            requestType: RequestType.Parallel,
            data: {
              alias,
            },
          });
        }
      });

    return () => {
      landingPageData$.next(null);
      courseStructure$.next(null);
      unsubscriber.next();
      unsubscriber.complete();
    };
  }, []);

  const getSectionComponent = (section: ILandingPageSections) => {
    const environment = getEnvironment();
    if (environment.includes('ai') && section.pageName === LandingPageSection.Footer) {
      return null;
    }
    const SectionComponent = Components[section.pageName as LandingPageSection];

    return (
      <SectionComponent
        key={section.pageId}
        data={
          section.pageName === LandingPageSection.CourseSection
            ? courseStructure
            : section.data
        }
        alias={alias}
      />
    );
  };

  return data && courseStructure ? (
    <>
      <SlHelmet
        title={i18n.t(`web-lelp.${alias}-meta-title`)}
        description={i18n.t(`web-lelp.${alias}-meta-description`)}
        imgUrl={
          data.metaData?.find(
            (el) => el.name === ILandingPageMetaType.imgUrl,
          ).content
        }
      />
      <main className="landing-page">
        {data.sections?.map((section) => getSectionComponent(section))}
      </main>
      <SlFooter />
    </>
  ) : null;
}
